import client from "@/utils/axios";

import { useCurrency } from "@/composables/useCurrency";

import { getRoundedDownValue } from "@/utils/helpers";
import { add, isZero } from "@/utils/math";

const getDefaultState = () => {
  return {
    summary: {},
    totals: [],
    aggregates: {},
    snapshots: [],
    stats: null,
    exchange: [],
    riskModels: [],
  };
};

const getRoundedValueNoSeparator = (value) => {
  return getRoundedDownValue(value ?? 0, null, false).replace(/,/g, "");
};

const hasDirection = (position) => {
  return (
    (position?.balance ?? 0) > 0 ||
    !isZero(getRoundedValueNoSeparator(position?.long)) ||
    !isZero(getRoundedValueNoSeparator(position?.short))
  );
};

const sortByDirectionExposure = (a, b) => {
  const aLong = Number(a?.long ?? 0);
  const aShort = Number(a?.short ?? 0);
  const bLong = Number(b?.long ?? 0);
  const bShort = Number(b?.short ?? 0);

  return (bLong + bShort) - (aLong + aShort);
};

export default {
  namespaced: true,

  state: getDefaultState(),

  actions: {
    resetStore({ commit }) {
      commit("resetStore");
    },

    async fetch({ commit }, payload = {}) {
      const { convertTo } = useCurrency();

      const { startDate, endDate, period } = payload;

      const params = { params: { convert_to: convertTo.value } };

      try {
        const { data } = await client.get("/api/risks/v1/risk_models");
        const riskModels = Array.isArray(data) ? data : [];
        const summaryEndpoint = riskModels.includes("pvar")
          ? "/api/risks/v1/pvar"
          : riskModels.includes("svf")
            ? "/api/risks/v1/svf"
            : "/api/risks/v1/ext";

        const [ext, aggregates, snapshots, exchange] = await Promise.all([
          client.get(summaryEndpoint, params),
          client.get("/api/risks/v1/aggregates", params),
          client.get("/api/risks/v1/ext/stats", {
            params: {
              start_date: startDate,
              end_date: endDate,
              period,
              ...params.params,
            },
          }),
          client.get("/api/risks/v1/snapshots/exchange_aggregate_usd", {
            params: {
              start_date: startDate,
              end_date: endDate,
              period,
            },
          }),
        ]);

        commit("setRiskModels", riskModels);
        commit("setSummary", ext?.data?.summary ?? {});
        commit("setTotals", ext?.data?.totals ?? []);
        commit("setAggregates", aggregates?.data ?? {});
        commit("setSnapshots", snapshots?.data?.raw ?? []);
        commit("setStats", snapshots?.data?.stats ?? null);
        commit("setExchange", exchange?.data ?? []);
      } catch {
        // do nothing
      }
    },

    async fetchExternalStats({ commit }, payload = {}) {
      const { convertTo } = useCurrency();
      const nextFilters = {
        startDate: payload.startDate,
        endDate: payload.endDate,
        period: payload.period
      };
      const { startDate, endDate, period } = nextFilters;

      try {
        const { data } = await client.get("/api/risks/v1/ext/stats", {
          params: {
            start_date: startDate,
            end_date: endDate,
            period,
            convert_to: convertTo.value,
          },
        });

        commit("setSnapshots", data?.raw ?? []);
        commit("setStats", data?.stats ?? null);
      } catch {
        // do nothing
      }
    },
  },

  getters: {
    assets(state) {
      return [...state.totals]
        .filter((item) => !isZero(getRoundedValueNoSeparator(item?.balance)))
        .sort((a, b) => (b?.balance ?? 0) - (a?.balance ?? 0))
        .map((item) => item.group_currency);
    },

    directionsAssets(state) {
      return [...state.totals]
        .filter((position) => hasDirection(position))
        .map((item) => item.group_currency);
    },

    sortedDirectionsAssets(state) {
      return [...state.totals]
        .filter((position) => hasDirection(position))
        .sort(sortByDirectionExposure)
        .map((item) => item.group_currency);
    },

    exchanges(state, _, __, rootGetters) {
      if (!state.aggregates?.exchanges) {
        return [];
      }

      return Object.keys(state.aggregates.exchanges)
        .filter((key) => !isZero(getRoundedValueNoSeparator(state.aggregates?.exchanges?.[key]?.balance)))
        .sort(
          (a, b) =>
            (state.aggregates?.exchanges?.[b]?.balance ?? 0) -
            (state.aggregates?.exchanges?.[a]?.balance ?? 0)
        )
        .map((key) => rootGetters.getExchangeFullName?.(key) ?? key);
    },

    plotHedgedRatio(state) {
      return [...state.totals]
        .filter((item) => !isZero(getRoundedValueNoSeparator(item?.balance)))
        .sort((a, b) => (b?.balance ?? 0) - (a?.balance ?? 0))
        .map((item) => (item?.hedged?.ratio ?? 0) * 100);
    },

    assetAllocation(state) {
      if (!Object.keys(state.aggregates ?? {}).length) {
        return [];
      }

      const assets = Object.entries(state.aggregates?.assets ?? {}).map(([group_currency, value]) => {
        return { group_currency, ...value };
      });

      return assets
        .filter((item) => !isZero(getRoundedValueNoSeparator(item?.balance)))
        .map((item) => item?.balance ?? 0)
        .sort((a, b) => b - a);
    },

    exchangeAllocation(state) {
      if (!state.aggregates?.exchanges) {
        return [];
      }

      return Object.keys(state.aggregates.exchanges)
        .filter((key) => !isZero(getRoundedValueNoSeparator(state.aggregates?.exchanges?.[key]?.balance)))
        .map((key) => state.aggregates?.exchanges?.[key]?.balance ?? 0)
        .sort((a, b) => b - a);
    },

    totalBalance(state, _, __, rootGetters) {
      const exchanges = [...new Set((state.exchange ?? []).map((item) => item.exchange_id))];
      const timestamps = [...new Set((state.exchange ?? []).map((item) => item.timestamp))];

      return exchanges.reduce((memo, exchangeId) => {
        memo.push({
          name: rootGetters.getExchangeFullName?.(exchangeId) ?? exchangeId,
          data: timestamps.map((timestamp) => {
            const exchange = state.exchange.find(
              (item) => item.timestamp === timestamp && item.exchange_id === exchangeId
            );

            return {
              x: timestamp * 1000,
              y: exchange?.exchange_balance ?? 0,
              name: rootGetters.getExchangeFullName?.(exchangeId) ?? exchangeId,
            };
          }),
        });

        return memo;
      }, []);
    },

    dailyPnl(state, getters) {
      if (getters.isAllSnapshotsEmpty) {
        return [];
      }

      return state.snapshots.map((item) => {
        return {
          x: item.timestamp * 1000,
          y: (item?.pnl_perc ?? 0) * 100,
          raw: item?.pnl ?? 0,
          fillColor: item?.pnl_perc < 0 ? "#ff2d2d" : "#1ed760",
        };
      });
    },

    histogramPnl(state) {
      const result = {
        frequency: [],
        normal: [],
      };

      if (state.stats === null) {
        return result;
      }

      result.frequency = state.stats.histogram.map((item) => {
        return {
          x: parseFloat(item[0]) * 100,
          y: parseFloat(item[1]),
        };
      });

      result.normal = state.stats.histogram.map((item) => {
        return {
          x: parseFloat(item[0]) * 100,
          y: parseFloat(item[2]),
        };
      });

      return result;
    },

    historicalNetBalances(state, getters) {
      if (getters.isAllSnapshotsEmpty) {
        return [];
      }

      return state.snapshots.map((item) => {
        return {
          x: item.timestamp * 1000,
          y: item?.net_balance ?? 0,
        };
      });
    },

    historicalBalances(state, getters) {
      if (getters.isAllSnapshotsEmpty) {
        return [];
      }

      return state.snapshots.map((item) => {
        return {
          x: item.timestamp * 1000,
          y: item?.balance ?? 0,
        };
      });
    },

    historicalDeposits(state, getters) {
      if (getters.isAllSnapshotsEmpty) {
        return [];
      }

      return state.snapshots.map((item) => {
        return {
          x: item.timestamp * 1000,
          y: item?.deposited ?? 0,
        };
      });
    },

    historicalWithdrawals(state, getters) {
      if (getters.isAllSnapshotsEmpty) {
        return [];
      }

      return state.snapshots.map((item) => {
        return {
          x: item.timestamp * 1000,
          y: item?.withdrawn ?? 0,
        };
      });
    },

    directions(state) {
      return state.totals
        .filter((position) => hasDirection(position))
        .reduce(
          (memo, current) => {
            memo.short.push(current?.short ? -current.short : 0);
            memo.long.push(current?.long ?? 0.00001);
            return memo;
          },
          { short: [], long: [] }
        );
    },

    sortedDirections(state) {
      const filtered = state.totals.filter((position) => hasDirection(position));
      filtered.sort(sortByDirectionExposure);

      return filtered.reduce(
        (memo, current) => {
          memo.short.push(current?.short ? -current.short : 0);
          memo.long.push(current?.long ?? 0.00001);
          return memo;
        },
        { short: [], long: [] }
      );
    },

    sumLong(state) {
      return state.totals.reduce((memo, current) => {
        return add(memo, current?.long ?? "0");
      }, "0");
    },

    sumShort(state) {
      return state.totals.reduce((memo, current) => {
        return add(memo, current?.short ?? "0");
      }, "0");
    },

    hedgedRatio(state, getters) {
      if (getters.isAllSnapshotsEmpty) {
        return [];
      }

      return state.snapshots.map((item) => {
        const hedgedSumValue = (item?.hedged ?? 0) + (item?.unhedged ?? 0);
        const hedgedSum = hedgedSumValue === 0 ? 1 : hedgedSumValue;

        return [item.timestamp * 1000, item?.hedged ? (item.hedged / hedgedSum) * 100 : 0];
      });
    },

    allSortedTotals(state) {
      return  [...state.totals].sort((a, b) => (b?.balance ?? 0) - (a?.balance ?? 0));
    },

    sortedTotals(state) {
      return [...state.totals]
        .filter((item) => (item?.balance ?? 0) > 0)
        .sort((a, b) => (b?.balance ?? 0) - (a?.balance ?? 0))
        .slice(0, 10);
    },

    liquidationRisks(state) {
      return state.totals.filter((item) => (item?.liquidation_ladder?.length ?? 0) > 0);
    },

    isAllSnapshotsEmpty(state) {
      return !Array.isArray(state.snapshots) || state.snapshots.length === 0;
    },

    filters(state) {
      return state.filters;
    },
  },

  mutations: {
    resetStore(state) {
      Object.assign(state, getDefaultState());
    },

    setSummary(state, summary) {
      state.summary = summary;
    },

    setTotals(state, totals) {
      state.totals = totals;
    },

    setAggregates(state, aggregates) {
      state.aggregates = aggregates;
    },

    setSnapshots(state, snapshots) {
      state.snapshots = [...snapshots].sort((a, b) => (a?.timestamp > b?.timestamp ? 1 : -1));
    },

    setStats(state, stats) {
      state.stats = stats;
    },

    setExchange(state, exchange) {
      state.exchange = exchange;
    },

    setRiskModels(state, riskModels) {
      state.riskModels = riskModels;
    },

    setFilters(state, filters) {
      state.filters = filters;
    },
  },
};
